/**
 * @file catalog-format.ts
 * @description Тексты ячеек зоны «Товары» (phase19.31): цена товара по
 * фасовкам, артикулы, остаток, дата правки. Деньги — «1 175,00 ₽» (SC43).
 *
 * Цена может не прийти (`null`): у товара нет активных фасовок или роль цен
 * продажи не видит (SC51, контракт §11) — тогда «—», а не ноль.
 */

import { CERTIFICATE_TYPE_LABELS } from "@chaiberi/shared";

import type { CatalogProduct, CatalogSku, CertificateType } from "./api-catalog";
import { formatRub } from "./crm-deal-view";
import { formatShopDate } from "./shop-time";

/** «350,00 ₽» — одна цена; «от 350,00 ₽» — у фасовок разные цены; «—» — цены нет. */
export function productPriceText(product: Pick<CatalogProduct, "priceMin" | "priceMax">): string {
  const min = product.priceMin ?? null;
  const max = product.priceMax ?? null;
  if (min === null) return "—";
  if (max !== null && Number(max) !== Number(min)) return `от ${formatRub(min)}`;
  return formatRub(min);
}

/** Цена фасовки: «—», если роль цену не видит. */
export function skuPriceText(sku: Pick<CatalogSku, "priceGross">): string {
  return sku.priceGross === null ? "—" : formatRub(sku.priceGross);
}

/**
 * Артикулы фасовок для второй линии: «RS-50 +1». Из `skuCodes` сервера
 * (действующие первыми, контракт §20) — не зависят от сводки остатка.
 */
export function productSkuCodes(product: Pick<CatalogProduct, "skuCodes">): string | null {
  const [first, ...rest] = product.skuCodes ?? [];
  if (first === undefined) return null;
  return rest.length === 0 ? first : `${first} +${rest.length}`;
}

/**
 * Дата коротко: «26.09.2026». Момент времени (дата правки) — день по поясу
 * магазина (SC31), а не браузера; чистая дата «гггг-мм-дд» (срок документа)
 * — как есть, без пересчёта поясов.
 */
export function shortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (plain) return `${plain[3]}.${plain[2]}.${plain[1]}`;
  return formatShopDate(iso);
}

/** Число без хвоста нулей: «13», «2,5». */
export function plainQty(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString("ru-RU", { maximumFractionDigits: 3 }) : value;
}

/**
 * Виды документа производителя — подпись в списке, в окне и на сайте одна:
 * берётся из shared `CERTIFICATE_TYPE_LABELS` (phase19.16, TD-486).
 */
export const CERT_LABELS: Readonly<Record<CertificateType, string>> = CERTIFICATE_TYPE_LABELS;
